import { getCookie, removeCookie, setCookie } from "typescript-cookie";
import { readPartnerLinkFromSearch } from "./attributionCapture";
import { PARTNER_LINK_TTL_DAYS, readUtmDatasCookie } from "./utmDatasCookie";

export type ConsentType = {
  ad_storage: "granted" | "denied";
  ad_user_data: "granted" | "denied";
  ad_personalization: "granted" | "denied";
  analytics_storage: "granted" | "denied";
  personalization_storage: "granted" | "denied";
  functionality_storage: "granted" | "denied";
  security_storage: "granted" | "denied";
};

const CONSENT_KEY = "consentMode";

const getParentDomain = (): string | undefined => {
  if (typeof window === "undefined") return undefined;
  const parts = window.location.hostname.split(".");
  if (parts.length > 2) return "." + parts.slice(parts.length - 2).join(".");
  return "." + window.location.hostname;
};

const readSharedConsent = (): ConsentType | null => {
  try {
    const cookieValue = getCookie(CONSENT_KEY);
    if (cookieValue) return JSON.parse(cookieValue as string) as ConsentType;
  } catch (e) {}

  if (typeof localStorage !== "undefined") {
    const local = localStorage.getItem(CONSENT_KEY);
    if (local) {
      try {
        return JSON.parse(local) as ConsentType;
      } catch (e) {}
    }
  }
  return null;
};

const clearSharedConsent = (): void => {
  if (typeof localStorage !== "undefined") localStorage.removeItem(CONSENT_KEY);

  if (typeof window !== "undefined" && window.navigator.cookieEnabled) {
    removeCookie(CONSENT_KEY, { domain: getParentDomain(), path: "/" });
    removeCookie(CONSENT_KEY, { path: "/" });
  }
};

const openConsentPreferences = (): void => {
  clearSharedConsent();
  if (typeof window !== "undefined")
    window.dispatchEvent(new Event("open-consent-preferences"));
};

const writeSharedConsent = (consent: ConsentType): void => {
  if (typeof localStorage !== "undefined")
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));

  if (typeof window !== "undefined" && window.navigator.cookieEnabled) {
    setCookie(CONSENT_KEY, JSON.stringify(consent), {
      expires: 365,
      domain: getParentDomain(),
      secure: true,
      sameSite: "lax",
      path: "/",
    });
  }
};

const utmManager = (searchParams: URLSearchParams): void => {
  const consent = readSharedConsent();
  if (
    !consent ||
    (consent.ad_storage !== "granted" &&
      consent.analytics_storage !== "granted")
  ) {
    return;
  }

  const partnerLink = readPartnerLinkFromSearch(searchParams);
  if (!partnerLink || !window.navigator.cookieEnabled) return;

  const existing = readUtmDatasCookie(getCookie("utm_datas"));
  if (existing.bp_id) return;

  // A host-only cookie shadows the landing's parent-domain utm_datas: carry its keys over.
  setCookie(
    "utm_datas",
    JSON.stringify({
      ...existing,
      bp_id: partnerLink.bp_id,
      bp_id_captured_at: partnerLink.capturedAt,
    }),
    {
      expires: PARTNER_LINK_TTL_DAYS,
      secure: true,
      sameSite: "lax",
      path: "/",
    }
  );
};

const Utils = {
  utmManager,
  readSharedConsent,
  writeSharedConsent,
  clearSharedConsent,
  openConsentPreferences,
};

if (typeof window !== "undefined") {
  (window as any).openConsentPreferences = openConsentPreferences;
}

export default Utils;
